import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, Button, TableCell, TableFooter, TableRow, Typography } from '@mui/material';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { FinancialConditionSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionSection.tsx';
import { FinancialConditionTable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTable.tsx';
import {
  INCOME_STRUCTURE_BY_KVED_TABLE_ARIA_LABEL,
  INCOME_STRUCTURE_BY_KVED_TABLE_COLUMN_WIDTHS,
  INCOME_STRUCTURE_BY_KVED_TABLE_HEADERS,
  INCOME_STRUCTURE_BY_KVED_TABLE_MIN_WIDTH,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/constants.ts';
import { IncomeStructureByKvedIncomeRow } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/IncomeStructureByKvedIncomeRow.tsx';
import type { TIncomeStructureByKvedFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';
import {
  createIncomeStructureByKvedIncome,
  getIncomeStructureByKvedTotal,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/utils.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

interface IIncomeStructureByKvedIncomesSectionProps {
  disabled: boolean;
}

export const IncomeStructureByKvedIncomesSection: React.FC<
  IIncomeStructureByKvedIncomesSectionProps
> = ({ disabled }) => {
  const { control } = useFormContext<TIncomeStructureByKvedFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'incomes' });
  const incomes = useWatch({ control, name: 'incomes' });
  const totalIncome = getIncomeStructureByKvedTotal(incomes ?? []);
  const showActions = fields.length > 1;

  return (
    <FinancialConditionSection
      title="Структура доходів"
      description="Дані наводяться за річною фінансовою звітністю за обраний звітний рік, у тисячах гривень."
    >
      <FinancialConditionTable
        ariaLabel={INCOME_STRUCTURE_BY_KVED_TABLE_ARIA_LABEL}
        headers={[...INCOME_STRUCTURE_BY_KVED_TABLE_HEADERS]}
        showActions={showActions}
        columnWidths={[...INCOME_STRUCTURE_BY_KVED_TABLE_COLUMN_WIDTHS]}
        minWidth={INCOME_STRUCTURE_BY_KVED_TABLE_MIN_WIDTH}
        footer={
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} align="right">
                <Typography component="span" sx={{ fontWeight: 800 }}>
                  Всього
                </Typography>
              </TableCell>
              <TableCell>
                <Typography component="span" sx={{ fontWeight: 800 }}>
                  {totalIncome}
                </Typography>
              </TableCell>
              {showActions && <TableCell />}
            </TableRow>
          </TableFooter>
        }
      >
        {fields.map((field, index) => (
          <IncomeStructureByKvedIncomeRow
            key={field.id}
            index={index}
            canRemove={showActions}
            disabled={disabled}
            onRemove={() => remove(index)}
          />
        ))}
      </FinancialConditionTable>
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => append(createIncomeStructureByKvedIncome())}
          disabled={disabled}
          sx={documentGenerationFormStyles.addButton}
        >
          Додати вид діяльності
        </Button>
      </Box>
    </FinancialConditionSection>
  );
};
